import pick from 'lodash-es/pick';
import React, { Component } from 'react';
import styles from './searchable-dropdown.component.module.scss';

interface SelectDropdownSyncedState<T> {
    selectedValue: T;
    values: T[];
    label?: string;
}

export interface SearchDropdownProps<T> extends SelectDropdownSyncedState<T> {
    style: 'primary' | 'light'
    labelFormatter?: (value: T) => React.JSX.Element;

    valueFormatter?: (value: T) => React.JSX.Element;
    dropdownFormatter?: (value: T) => React.JSX.Element;
    filter: (value: T, matcher: RegExp) => boolean;
    hasTooltip?: (value: T) => boolean;
    getTooltip?: (value: T) => string;
    btnClass?: string; //TODO Preact not used
    onSelect?: (selectedValue: T) => void;
    onStateChanged?: (selectedValue: SelectDropdownSyncedState<T>) => void
}

interface SearchableDropdownState<T> extends SelectDropdownSyncedState<T> {
    shown: boolean;
    filterValue: string
}

/**
 * In react, its really hard to update a component based on this component parent's child's state.
 * This component is strongly coupled with the parent to overcome above issue. To use this component,
 * Use onStateChanged to observe the state changes and then pass down the state from props to this component.
 * Then on this component's `getDerivedStateFromProps` method, we copy the props from parent back to state.
 * 
 * This allow us to update this component's state from outside of the component. While not best practice,
 * our use case require this scenario.
 */
export class SearchableDropdown<T> extends Component<SearchDropdownProps<T>, SearchableDropdownState<T>> {
    wrapperRef = React.createRef<HTMLDivElement>();

    readonly state: Readonly<SearchableDropdownState<T>> = {
        selectedValue: this.props.selectedValue,
        label: this.props.label,
        values: this.props.values,
        shown: false,
        filterValue: ""
    };
    filterRef = React.createRef<HTMLInputElement>();

    private handleClickOutside = (e: MouseEvent) => {
        if (this.wrapperRef.current && !this.wrapperRef.current.contains(e.target as Node)) {
            this.setState({ shown: false, filterValue: "" });
        }
    };

    constructor(props: SearchDropdownProps<T>) {
        super(props);
    }

    static getDerivedStateFromProps<T>(
        props: SearchDropdownProps<T>,
        state: SearchableDropdownState<T>
    ): Partial<SearchableDropdownState<T>> {
        return {
            ...pick(props, ['label', 'values', 'selectedValue']),
        };
    }

    componentDidMount(): void {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount(): void {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    private toggleDropdown() {
        const opening = !this.state.shown;
        this.setState({ shown: opening, filterValue: opening ? this.state.filterValue : "" }, () => {
            if (this.state.shown && this.filterRef.current) {
                this.filterRef.current.focus();
            }
        });
    }

    updateSelectedValue(selectedValue: T) {
        this.setState({ selectedValue, shown: false, filterValue: "" });
        if (this.props.onStateChanged) {
            this.props.onStateChanged({
                ...pick(this.state, ['label', 'values', 'selectedValue']),
                selectedValue,
            });
        }
        if (this.props.onSelect) {
            this.props.onSelect(selectedValue);
        }
    }

    filterValueChanged() {
        const filterValue = this.filterRef.current.value;
        this.setState({ filterValue: filterValue || "" });
    }

    getFilteredValues() {
        if((this.state.filterValue || "").length == 0) {
            return this.props.values;
        }
        return this.props.values.filter(i => this.props.filter(i, new RegExp(this.state.filterValue, "i")))
    }

    getDropdownButtonContent() {
        return <div className={styles['btn-content']}>
            <div className={styles['label']}>
                {this.props.label || (this.props.labelFormatter && this.props.labelFormatter(this.state.selectedValue))}
            </div>
            <div className={styles['value']}>
                {
                    (!!this.props.valueFormatter) ? <div>
                        {this.props.valueFormatter(this.state.selectedValue)}
                    </div> : ''
                }
                <div className={styles['arrow']}>
                    <svg xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="tabler-icon tabler-icon-chevron-down stroke-icon !size-icon-xs">
                        <path d="M6 9l6 6l6 -6">
                        </path>
                    </svg>
                </div>
            </div>
        </div>
    }

    renderDropdownHeader() {
        return <input
            placeholder='Search...'
            ref={this.filterRef}
            onInput={() => this.filterValueChanged()}
            value={this.state.filterValue}
        />
    }

    renderDropdownContent() {
        return this.state.shown && this.getFilteredValues().map((val, index) => {
            return (
                <div key={index}
                    onClick={() => this.updateSelectedValue(val)}
                    className={`${styles['dropdown-item']} ${styles[index % 2 == 0 ? 'even' : 'odd']}`}>
                    <div className={styles['dropdown-menu-item']}>
                        <div>
                            {this.props.dropdownFormatter(val)}
                        </div>
                        <div>
                            {
                                this.state.selectedValue == val && <svg xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="tabler-icon tabler-icon-check stroke-icon size-icon-xs">
                                    <path d="M5 12l5 5l10 -10"></path>
                                </svg>
                            }
                        </div>
                    </div>
                </div>
            );
        })
    }

    render() {
        return <div className={styles['btn-group']} ref={this.wrapperRef}>
            <button className={`dropdown-no-arrow ${styles['dropdown-btn']}`}
                type="button"
                onClick={() => this.toggleDropdown()}
                aria-expanded={this.state.shown}>
                {this.getDropdownButtonContent()}
            </button>
            {this.state.shown && (
                <div className={`${styles['dropdown-menu']} ${styles['show']}`}>
                    <div className={styles['menu-header']}>
                        {this.renderDropdownHeader()}
                    </div>
                    <div className={styles['menu-content']}>
                        {this.renderDropdownContent()}
                    </div>
                </div>
            )}
        </div>
    }
}
